import React from "react";
import { formatDate, UNIT_SUFFIX, UNIT_MS } from "../lib/api";

/**
 * Simple SVG Gantt visualization. Expects enriched tasks from computePlan (with _planStart/_planEnd).
 * `timeUnit` ("Minutes" | "Hours" | "Days" | "Weeks") drives tick granularity, tick labels, and
 * the duration suffix on each bar so an "Hours" project renders hour ticks + "4h" labels, etc.
 */
export default function Gantt({ tasks, timeUnit = "Days" }) {
  if (!tasks || tasks.length === 0) {
    return (
      <div className="pc-card p-8 mt-6 text-center text-slate-400" data-testid="gantt-empty">
        Add tasks to see the Gantt timeline.
      </div>
    );
  }

  const starts = tasks.map((t) => t._planStart?.getTime()).filter(Boolean);
  const ends = tasks.map((t) => t._planEnd?.getTime()).filter(Boolean);
  if (starts.length === 0 || ends.length === 0) return null;
  const min = Math.min(...starts);
  const max = Math.max(...ends);
  const span = Math.max(max - min, 1);
  const rowHeight = 32;
  const leftLabelW = 220;
  const chartW = 900;
  const barAreaW = chartW - leftLabelW - 20;
  const height = tasks.length * rowHeight + 56;

  // Tick granularity matches the project's time unit — aim for ~10 ticks.
  const unitMs = UNIT_MS[timeUnit] || UNIT_MS.Days;
  const totalUnits = Math.max(1, Math.ceil(span / unitMs));
  const tickEvery = Math.max(1, Math.ceil(totalUnits / 10));

  const fmtTick = (d) => {
    if (timeUnit === "Minutes" || timeUnit === "Hours") {
      try { return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
      catch { return d.toISOString().slice(11, 16); }
    }
    return formatDate(d);
  };

  const ticks = [];
  for (let i = 0; i <= totalUnits; i += tickEvery) {
    const t = min + i * unitMs;
    ticks.push({ t, x: leftLabelW + ((t - min) / span) * barAreaW });
  }

  const suffix = UNIT_SUFFIX[timeUnit] || "d";
  const taskById = new Map(tasks.map((t) => [t.task_number, t]));

  return (
    <div className="pc-card p-5 mt-6 overflow-x-auto" data-testid="gantt-chart" data-time-unit={timeUnit}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-300/80">Timeline · {timeUnit}</div>
          <div className="text-lg font-semibold text-white" style={{ fontFamily: "Outfit" }}>Project Gantt</div>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded bg-rose-500"></span> Critical</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded bg-blue-500"></span> Plan</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded bg-cyan-500"></span> Sprint</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded bg-emerald-500/70"></span> Completed</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded bg-amber-500/70"></span> In progress</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${chartW} ${height}`} width="100%" style={{ minWidth: chartW }}>
        {/* Time ticks — scale with the project's time unit */}
        {ticks.map((t) => (
          <g key={`tick-${t.t}`}>
            <line x1={t.x} x2={t.x} y1={30} y2={height - 10} stroke="rgba(148,163,184,0.12)" />
            <text x={t.x} y={22} fontSize={10} fill="#94a3b8" textAnchor="middle">
              {fmtTick(new Date(t.t))}
            </text>
          </g>
        ))}
        {/* Rows */}
        {tasks.map((t, idx) => {
          if (!t._planStart || !t._planEnd) return null;
          const x = leftLabelW + ((t._planStart.getTime() - min) / span) * barAreaW;
          const w = Math.max(4, ((t._planEnd.getTime() - t._planStart.getTime()) / span) * barAreaW);
          const y = 40 + idx * rowHeight;
          const status = (t.status || "Not Started").toLowerCase();
          const isCritical = !!t._critical;
          const color = t.is_sprint && status !== "completed" && status !== "in progress" ? "#06b6d4" : isCritical ? "#f43f5e" : status === "completed" ? "#10b981" : status === "in progress" ? "#f59e0b" : status === "blocked" ? "#f43f5e" : "#3b82f6";
          const pct = Number(t.actual_time || 0) > 0 && Number(t.estimated_time || 0) > 0
            ? Math.min(100, (Number(t.actual_time) / Number(t.estimated_time)) * 100)
            : status === "completed" ? 100 : 0;
          return (
            <g key={t.id} data-testid={`gantt-row-${t.id}`}>
              <text x={10} y={y + 14} fontSize={11} fill={isCritical ? "#fecaca" : "#cbd5e1"} style={{ fontFamily: "Plus Jakarta Sans" }}>
                <tspan fill={isCritical ? "#f43f5e" : "#64748b"}>{t.task_number}.</tspan> {trim(t.name, 24)}{isCritical && " ◆"}
              </text>
              <rect x={leftLabelW} y={y + 4} width={barAreaW} height={rowHeight - 10} fill="rgba(148,163,184,0.05)" rx={6} />
              {/* dependency connector */}
              {t.order && taskById.get(Number(t.order)) && taskById.get(Number(t.order))._planEnd && (() => {
                const pred = taskById.get(Number(t.order));
                const predX = leftLabelW + ((pred._planEnd.getTime() - min) / span) * barAreaW;
                const predY = 40 + tasks.findIndex((x) => x.id === pred.id) * rowHeight + rowHeight / 2;
                const bothCritical = isCritical && !!pred._critical;
                return (
                  <path
                    d={`M ${predX} ${predY} L ${predX + 6} ${predY} L ${predX + 6} ${y + rowHeight / 2} L ${x} ${y + rowHeight / 2}`}
                    fill="none"
                    stroke={bothCritical ? "rgba(244,63,94,0.8)" : "rgba(59,130,246,0.5)"}
                    strokeWidth={bothCritical ? 1.5 : 1}
                  />
                );
              })()}
              {/* Plan bar */}
              <rect x={x} y={y + 6} width={w} height={rowHeight - 14} rx={6} fill={color} opacity={0.9}
                stroke={t.is_sprint && isCritical ? "#f43f5e" : "none"} strokeWidth={t.is_sprint && isCritical ? 2 : 0} />
              {pct > 0 && (
                <rect x={x} y={y + 6} width={(w * pct) / 100} height={rowHeight - 14} rx={6} fill="#fff" opacity={0.18} />
              )}
              <text x={x + 6} y={y + rowHeight / 2 + 3} fontSize={10} fill="#fff">
                {t._estDays}{suffix}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function trim(s, n) { if (!s) return ""; return s.length > n ? s.slice(0, n - 1) + "…" : s; }
